// src/redux/services/screenshotApi.js
// 📹 SCREENSHOT API - RTK Query

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || 'https://qylmox.in/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem('token');
    if (token) headers.set('x-auth-token', token);
    return headers;
  },
});

export const screenshotApi = createApi({
  reducerPath: 'screenshotApi',
  baseQuery,
  tagTypes: ['Screenshots', 'ScreenshotStats'],
  endpoints: (builder) => ({

    // ===== CAPTURE SCREENSHOT =====
    captureScreenshot: builder.mutation({
      query: (data) => ({
        url: '/screenshots/capture',
        method: 'POST',
        body: {
          cameraId: data.cameraId,
          captureSource: data.captureSource || 'manual',
        },
      }),
      invalidatesTags: ['Screenshots', 'ScreenshotStats'],
    }),

    // ===== GET SCREENSHOTS BY DEVICE (Paginated) =====
    getScreenshotsByDevice: builder.query({
      query: ({ deviceId, page = 1, limit = 20, startDate, endDate, cameraId, status }) => ({
        url: `/screenshots/device/${deviceId}`,
        params: {
          page,
          limit,
          startDate,
          endDate,
          cameraId,
          status,
        },
      }),
      providesTags: (result, error, { deviceId }) => [
        { type: 'Screenshots', id: deviceId },
      ],
      transformResponse: (response) => ({
        screenshots: response?.data || [],
        total: response?.total || 0,
        page: response?.page || 1,
        pages: response?.pages || 1,
      }),
    }),

    // ===== GET SCREENSHOTS BY CAMERA =====
    getScreenshotsByCamera: builder.query({
      query: ({ cameraId, page = 1, limit = 20 }) => ({
        url: `/screenshots/camera/${cameraId}`,
        params: { page, limit },
      }),
      providesTags: (result, error, { cameraId }) => [
        { type: 'Screenshots', id: `camera-${cameraId}` },
      ],
      transformResponse: (response) => ({
        screenshots: response?.data || [],
        total: response?.total || 0,
        page: response?.page || 1,
        pages: response?.pages || 1,
      }),
    }),

    // ===== GET SINGLE SCREENSHOT =====
    getScreenshot: builder.query({
      query: (screenshotId) => `/screenshots/${screenshotId}`,
      transformResponse: (response) => response?.data || response,
    }),

    // ===== GET SCREENSHOT STATS FOR DEVICE =====
    getScreenshotStats: builder.query({
      query: (deviceId) => `/screenshots/device/${deviceId}/stats`,
      providesTags: (result, error, deviceId) => [
        { type: 'ScreenshotStats', id: deviceId },
      ],
      transformResponse: (response) => response?.data || {},
    }),

    // ===== RETRY FAILED SCREENSHOT =====
    retryScreenshot: builder.mutation({
      query: (screenshotId) => ({
        url: `/screenshots/${screenshotId}/retry`,
        method: 'POST',
      }),
      invalidatesTags: ['Screenshots'],
    }),

    // ===== DELETE SCREENSHOT =====
    deleteScreenshot: builder.mutation({
      query: ({ screenshotId, deleteFile = true }) => ({
        url: `/screenshots/${screenshotId}`,
        method: 'DELETE',
        params: { deleteFile },
      }),
      invalidatesTags: ['Screenshots', 'ScreenshotStats'],
    }),

    // ===== CLEANUP OLD SCREENSHOTS (Admin) =====
    cleanupOldScreenshots: builder.mutation({
      query: ({ daysOld = 30, deleteFiles = true }) => ({
        url: '/screenshots/cleanup',
        method: 'DELETE',
        params: { daysOld, deleteFiles },
      }),
      invalidatesTags: ['Screenshots', 'ScreenshotStats'],
    }),

  }),
});

export const {
  useCaptureScreenshotMutation,
  useGetScreenshotsByDeviceQuery,
  useGetScreenshotsByCameraQuery,
  useGetScreenshotQuery,
  useGetScreenshotStatsQuery,
  useRetryScreenshotMutation,
  useDeleteScreenshotMutation,
  useCleanupOldScreenshotsMutation,
} = screenshotApi;